import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function calculateTimePassed(timestamp, t) {
  const now = new Date();
  const past = new Date(timestamp);
  const diffInSeconds = Math.floor((now - past) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);

  if (diffInSeconds < 10) {
    return { text: t('time.now'), nextUpdate: 1 }; // Update every second
  }

  if (diffInSeconds < 60) {
    return {
      text: t(diffInSeconds === 1 ? 'time.second_ago' : 'time.seconds_ago', { count: diffInSeconds }),
      nextUpdate: 1,
    };
  }

  if (diffInMinutes < 60) {
    return {
      text: t(diffInMinutes === 1 ? 'time.minute_ago' : 'time.minutes_ago', { count: diffInMinutes }),
      nextUpdate: 60 - (diffInSeconds % 60), // Next update at the next minute
    };
  }

  if (diffInHours < 24) {
    return {
      text: t(diffInHours === 1 ? 'time.hour_ago' : 'time.hours_ago', { count: diffInHours }),
      nextUpdate: 3600 - (diffInSeconds % 3600), // Next update at the next hour
    };
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return {
      text: t(diffInDays === 1 ? 'time.day_ago' : 'time.days_ago', { count: diffInDays }),
      nextUpdate: 86400 - (diffInSeconds % 86400), // Next update at the next day
    };
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return {
      text: t(diffInMonths === 1 ? 'time.month_ago' : 'time.months_ago', { count: diffInMonths }),
      nextUpdate: Infinity, // No need to update
    };
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return {
    text: t(diffInYears === 1 ? 'time.year_ago' : 'time.years_ago', { count: diffInYears }),
    nextUpdate: Infinity, // No need to update
  };
}

export const TimePassedComponent = ({ timestamp }) => {
  const { t } = useTranslation('common')
  const [timePassed, setTimePassed] = useState(() => calculateTimePassed(timestamp, t));

  useEffect(() => {
    if (timePassed.nextUpdate === Infinity) {
      return; // No updates needed for long durations like months or years
    }

    const interval = setInterval(() => {
      setTimePassed(calculateTimePassed(timestamp, t));
    }, timePassed.nextUpdate * 1000);

    return () => clearInterval(interval); // Cleanup on unmount or re-render
  }, [timePassed, timestamp]);

  return <span>{timePassed.text}</span>;
};
